import {FIELD_BIOME_LAYER_KEYS, FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {GEYSER_STEAM_KIND} from '../../../configs/biomeEffects/geyserSteam';
import {resolveSpawnBox} from './biomeContext';
import {
  TWO_PI,
  expandBox,
  hash01,
  lerp,
  pickPointInBox,
  pickPointInOval,
  pickWeighted,
  randomIn,
  randomInt,
  randomRange,
  randomSign,
  resolveLifeEnvelope,
  smoothstep,
  toRadians,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * ГЕЙЗЕРЫ И ПАРЯЩИЕ ИСТОЧНИКИ (гейзерная подложка). Чистые функции без Phaser и
 * DOM: по этому описанию частицы каждый кадр ставит сцена (scenes/FieldBiomeEffects).
 *
 * Все привязано к нарисованному: гейзеры, источники и озерца стоят на своих
 * местах картинки пейзажа, размеры — доли ее стороны. Три дорожки нарисованного:
 *  - столбы: постоянная группа. По каждому нарисованному столбу один за другим
 *    поднимаются бледные клубы — столб дышит. Клуб ходит по кругу: сдвиг вбок и
 *    плотность на каждом круге — функция номера круга;
 *  - извержения: время от времени один из гейзеров бьет ярче и выше — струя
 *    быстрых клубов шире нарисованного столба, облака пара, оторвавшиеся от
 *    столба и уходящие по ветру матча, и водяная пыль, которую струя уносит
 *    вверх: капли летят вдоль столба, замедляются и тают на лету;
 *  - источники: струйки пара над источниками и темными озерцами, каждая
 *    струйка — своя группа из нескольких клубов внахлест.
 *
 * Гейзеров и источников больше, чем помещается в кадр, поэтому охотнее
 * извергается и парит то, на что смотрит камера.
 *
 * Все это лежит на земле (слой GROUND): пар нарисованного пейзажа жетоны и
 * отряды закрывают, а край картинки гасит сам.
 *
 * Четвертая дорожка — трещины корки на самом поле: гейзеры и озерца лежат
 * вокруг поля и в кадр попадают не всегда. Струйка пара поднимается там, куда
 * смотрит камера; белый клуб на светлой корке выдает его темная тень на земле.
 * Струйка — конус с вершиной в трещине: клуб рождается точкой и ширится, пока
 * поднимается. Слой — над дымкой пустых ячеек, под жетонами (CELL).
 *
 * У ПАРА ЕСТЬ ЗВУК — шипение (см. scenes/FieldEffectSounds). Шипит то, что
 * ВЫПУСКАЕТ пар: извержение гейзера и струйка из трещины на поле — их дорожки
 * помечены (GEYSER_ERUPTION_TAG, GEYSER_FUMAROLE_TAG), по меткам сцена находит
 * их живые группы и шипит, когда они в кадре игрока. Рождаться им метки не
 * мешают: сцена их не придерживает. Дыхание столбов и струйки над источниками
 * и озерцами не кончаются никогда — это фон, они без метки и молчат.
 * Группа извержения живет дольше самой струи (доживают оторвавшиеся облака
 * пара), поэтому бьет ли струя, сцена узнает отдельно — по ее силе
 * (resolveGeyserEruptionStrength): облака без струи не шипят.
 */

/** Метка дорожки извержений гейзеров: по ней сцена находит идущее извержение */
export const GEYSER_ERUPTION_TAG = 'geyserEruption';
/** Метка дорожки струек пара из трещин на поле: по ней сцена находит живые струйки */
export const GEYSER_FUMAROLE_TAG = 'geyserFumarole';

/**
 * Сила струи извержения в момент времени: 0 — струя не бьет (еще не начала
 * или уже спала, хотя группа жива: доживают облака пара), 1 — бьет в полную
 * силу. Нарастает, держится и спадает — это та самая доля, на которую умножена
 * плотность клубов струи: по ней сцена узнает, что гейзер и вправду извергается.
 *
 * @param {{buildUpMs: number, jetEndMs: number, settleMs: number}} group — группа извержения
 * @param {number} elapsedMs — сколько прошло с рождения группы
 * @returns {number} 0..1
 */
export const resolveGeyserEruptionStrength = (group, elapsedMs) => (
  smoothstep(0, group.buildUpMs, elapsedMs)
  * (1 - smoothstep(group.jetEndMs, group.jetEndMs + group.settleMs, elapsedMs))
);

const COLUMN = 'column';
const JET = 'jet';
const CLOUD = 'cloud';
const SPRAY = 'spray';
const WISP = 'wisp';
const FUMAROLE = 'fumarole';
const FUMAROLE_SHADOW = 'fumaroleShadow';

const LAYER = FIELD_BIOME_LAYER_KEYS.GROUND;
// Сколько раз струйка ищет себе место над игровыми ячейками
const FUMAROLE_ATTEMPTS = 8;

const checkIfPointInBox = (box, {x, y}) => x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;

/** Угловая скорость клуба: до заданной, в любую сторону; радиан в миллисекунду */
const pickSpin = (rng, degPerSecond) => (toRadians(degPerSecond) / 1000) * randomRange(rng, 0.4, 1) * randomSign(rng);

/**
 * Поворот вытянутого клуба: длинной стороной (высотой кадра) вдоль направления,
 * с небольшим случайным наклоном и как придется вверх ногами — клубы одной
 * текстуры не выглядят одинаковыми
 */
const pickStretchedRotation = (rng, direction, tiltDeg) => (
  Math.atan2(-direction.x, direction.y) + toRadians(randomRange(rng, -tiltDeg, tiltDeg)) + (rng() < 0.5 ? Math.PI : 0)
);

const createTracks = (context, params) => {
  const {side, wind, hexSize, rect, coverage} = context;
  const {columns, eruption, springs, fumaroles} = params;
  const {jet, cloud, spray} = eruption;

  // Нарисованные столбы гейзеров в координатах мира
  const geysers = params.geysers.map((geyser, index) => {
    const base = context.toWorld(geyser.base.u, geyser.base.v);
    const top = context.toWorld(geyser.top.u, geyser.top.v);
    const length = Math.hypot(top.x - base.x, top.y - base.y);
    const along = {x: (top.x - base.x) / length, y: (top.y - base.y) / length};
    return {
      index, base, top, length, along,
      across: {x: -along.y, y: along.x},
      width: geyser.widthShare * side,
      cloudHeight: geyser.cloudHeight,
    };
  });

  // Источники и озерца в координатах мира
  const anchors = springs.anchors.map((anchor, index) => {
    const lean = params.riseLean * (anchor.u - 0.5);
    const norm = Math.hypot(lean, 1);
    return {
      index,
      kind: anchor.kind,
      // Озерцо нарисовано под углом — овал, сплющенный по вертикали
      oval: context.toWorldOval({
        u: anchor.u, v: anchor.v, radiusU: anchor.radius, radiusV: anchor.radius * springs.flatten,
      }),
      // Вверх по экрану и чуть в сторону от середины картинки
      rise: {x: lean / norm, y: -1 / norm},
    };
  });

  /** Что видит камера, с запасом; null — камеры нет */
  const resolveWatchedBox = (view) => (view ? expandBox(view, hexSize * params.viewMarginFactor) : null);
  /** Вес выбора: то, на что смотрит камера, выбирается охотнее */
  const resolveViewWeight = (watchedBox, points, inViewWeight) => (
    watchedBox && points.some((point) => checkIfPointInBox(watchedBox, point)) ? inViewWeight : 1
  );

  // ------------------------------------------------------------------ столбы

  const buildColumns = ({rng}) => {
    const particles = [];
    geysers.forEach((geyser) => {
      // Дыхание — одно на весь столб
      const breathMs = randomIn(rng, columns.breathMs);
      const breathPhase = rng() * TWO_PI;
      for (let index = 0; index < columns.puffCount; index += 1) {
        particles.push({
          kind: COLUMN,
          layer: LAYER,
          texture: FIELD_BIOME_TEXTURES.PUFF,
          tint: columns.tint,
          geyser,
          // Зерно клуба: по нему и номеру круга выбираются сдвиг вбок и плотность
          seed: 1 + geyser.index * 17 + index + rng(),
          riseMs: randomIn(rng, columns.riseMs),
          // Клубы идут друг за другом, а не строем
          phase: (index + rng()) / columns.puffCount,
          breathMs,
          breathPhase,
          rotation: pickStretchedRotation(rng, geyser.along, columns.tiltDeg),
        });
      }
    });
    return {durationMs: Infinity, particles};
  };

  const resolveColumnPose = (group, particle, elapsedMs, out) => {
    const {geyser} = particle;
    const turn = elapsedMs / particle.riseMs + particle.phase;
    const cycle = Math.floor(turn);
    const life = turn - cycle;
    const sway = (hash01(particle.seed, cycle) - 0.5) * geyser.width * columns.swayShare * life;
    const breath = 1 - columns.breathDepth
      * (0.5 + 0.5 * Math.sin((TWO_PI * elapsedMs) / particle.breathMs + particle.breathPhase));
    const alpha = lerp(columns.alpha.min, columns.alpha.max, hash01(particle.seed + 0.61, cycle))
      * resolveLifeEnvelope(life, columns.fadeInShare, columns.fadeOutShare) * breath;
    const width = geyser.width * lerp(columns.widthScale.from, columns.widthScale.to, life);
    return writePose(
      out,
      geyser.base.x + geyser.along.x * geyser.length * life + geyser.across.x * sway,
      geyser.base.y + geyser.along.y * geyser.length * life + geyser.across.y * sway,
      alpha, width, width * columns.stretch,
      particle.rotation
    );
  };

  // -------------------------------------------------------------- извержения

  const buildEruption = ({rng, view, liveGroups}) => {
    // Гейзер, который уже извергается, второй раз разом не бьет
    const busy = new Set(liveGroups.map((group) => group.geyserIndex));
    const watchedBox = resolveWatchedBox(view);
    const geyser = pickWeighted(
      rng,
      geysers.filter(({index}) => !busy.has(index)),
      ({base, top}) => resolveViewWeight(watchedBox, [base, top], eruption.inViewWeight)
    );
    if (!geyser) {
      return null;
    }

    const jetMs = randomIn(rng, eruption.jetMs);
    const jetEndMs = eruption.buildUpMs + jetMs;
    const particles = [];
    for (let index = 0; index < jet.puffCount; index += 1) {
      particles.push({
        kind: JET,
        layer: LAYER,
        sublayer: 0,
        texture: FIELD_BIOME_TEXTURES.PUFF,
        tint: jet.tint,
        // Зерно клуба: по нему и номеру круга — сдвиг вбок и плотность
        seed: 1 + index + rng(),
        riseMs: randomIn(rng, jet.riseMs),
        phase: (index + rng()) / jet.puffCount,
        rotation: pickStretchedRotation(rng, geyser.along, jet.tiltDeg),
      });
    }

    let durationMs = jetEndMs + eruption.settleMs;
    for (let index = 0; index < cloud.count; index += 1) {
      // Облака отрываются по очереди, на протяжении струи
      const bornShare = lerp(cloud.bornShare.min, cloud.bornShare.max, (index + rng()) / cloud.count);
      const bornMs = eruption.buildUpMs + jetMs * bornShare;
      const lifeMs = randomIn(rng, cloud.lifeMs);
      const height = geyser.length * randomIn(rng, geyser.cloudHeight);
      const jitter = (rng() - 0.5) * geyser.width * cloud.jitterShare;
      particles.push({
        kind: CLOUD,
        layer: LAYER,
        sublayer: 1,
        texture: FIELD_BIOME_TEXTURES.PUFF,
        tint: cloud.tint,
        bornMs,
        lifeMs,
        x: geyser.base.x + geyser.along.x * height + geyser.across.x * jitter,
        y: geyser.base.y + geyser.along.y * height + geyser.across.y * jitter,
        lift: side * randomIn(rng, cloud.liftShare),
        drift: side * randomIn(rng, cloud.driftShare),
        startRad: rng() * TWO_PI,
        spinRadPerMs: pickSpin(rng, cloud.spinDegPerSecond),
      });
      durationMs = Math.max(durationMs, bornMs + lifeMs);
    }

    const sprayCount = randomInt(rng, spray.count.min, spray.count.max);
    for (let index = 0; index < sprayCount; index += 1) {
      particles.push({
        kind: SPRAY,
        layer: LAYER,
        sublayer: 2,
        texture: FIELD_BIOME_TEXTURES.DOT,
        tint: spray.tint,
        // Зерно капли: по нему и номеру круга — откуда она срывается и куда летит
        seed: 1 + index + rng(),
        lifeMs: randomIn(rng, spray.lifeMs),
        phase: rng(),
      });
    }

    return {
      durationMs,
      geyser,
      geyserIndex: geyser.index,
      // По этим трем числам считается сила струи (resolveGeyserEruptionStrength) —
      // и здесь, и в сцене, которой нужно знать, бьет ли струя
      buildUpMs: eruption.buildUpMs,
      jetEndMs,
      settleMs: eruption.settleMs,
      pulsePhase: rng() * TWO_PI,
      particles,
    };
  };

  /** До какой высоты (в длинах столба) бьет струя в момент времени: растет с силой и ходит толчками */
  const resolveJetReach = (group, elapsedMs, strength) => (
    lerp(jet.startReach, jet.reach, strength)
    * (1 + jet.pulseDepth * Math.sin((TWO_PI * elapsedMs) / jet.pulseMs + group.pulsePhase))
  );

  const resolveEruptionPose = (group, particle, elapsedMs, out) => {
    const {geyser} = group;

    if (particle.kind === CLOUD) {
      const life = (elapsedMs - particle.bornMs) / particle.lifeMs;
      if (!(life > 0) || !(life < 1)) {
        return writeHiddenPose(out);
      }
      // Облако быстро разбухает и поднимается, потом только плывет по ветру
      const ease = 1 - (1 - life) ** cloud.easePower;
      const size = side * lerp(cloud.sizeShare.from, cloud.sizeShare.to, ease);
      return writePose(
        out,
        particle.x + geyser.along.x * particle.lift * ease + wind.x * particle.drift * life,
        particle.y + geyser.along.y * particle.lift * ease + wind.y * particle.drift * life,
        cloud.alpha * smoothstep(0, cloud.fadeInShare, life) * (1 - life) ** cloud.fadePower,
        size, size,
        particle.startRad + particle.spinRadPerMs * elapsedMs
      );
    }

    // Сила струи — та же, по которой сцена узнает, что гейзер извергается
    const strength = resolveGeyserEruptionStrength(group, elapsedMs);
    if (!(strength > 0)) {
      return writeHiddenPose(out);
    }

    if (particle.kind === JET) {
      const reach = geyser.length * resolveJetReach(group, elapsedMs, strength);
      const turn = elapsedMs / particle.riseMs + particle.phase;
      const cycle = Math.floor(turn);
      const life = turn - cycle;
      // Струя расходится кверху
      const spread = (hash01(particle.seed, cycle) - 0.5) * geyser.width * jet.spreadShare * life;
      const width = geyser.width * lerp(jet.widthScale.from, jet.widthScale.to, life);
      return writePose(
        out,
        geyser.base.x + geyser.along.x * reach * life + geyser.across.x * spread,
        geyser.base.y + geyser.along.y * reach * life + geyser.across.y * spread,
        lerp(jet.alpha.min, jet.alpha.max, hash01(particle.seed + 0.61, cycle))
          * resolveLifeEnvelope(life, jet.fadeInShare, jet.fadeOutShare) * strength,
        width, width * jet.stretch,
        particle.rotation
      );
    }

    // Водяная пыль: струя уносит каплю вверх вдоль столба, капля замедляется и
    // тает на лету. Дугой в сторону и обратно вниз она не летит: так пыль
    // читалась фонтанчиком
    const sprayStrength = smoothstep(spray.minStrength, spray.minStrength + spray.strengthFade, strength);
    if (!(sprayStrength > 0)) {
      return writeHiddenPose(out);
    }
    const turn = elapsedMs / particle.lifeMs + particle.phase;
    const cycle = Math.floor(turn);
    const life = turn - cycle;
    // Капля срывается с той высоты, до которой струя била в миг ее рождения:
    // дальше она летит сама и за толчками струи не ходит
    const bornAtMs = elapsedMs - life * particle.lifeMs;
    const bornReach = geyser.length
      * resolveJetReach(group, bornAtMs, resolveGeyserEruptionStrength(group, bornAtMs));
    const from = bornReach * lerp(spray.startShare.min, spray.startShare.max, hash01(particle.seed + 0.23, cycle));
    // Выше потолка пыль не поднимается: у капли, которой до него близко, подъем короче
    const rise = Math.min(
      geyser.length * lerp(spray.riseShare.min, spray.riseShare.max, hash01(particle.seed + 0.73, cycle)),
      geyser.length * spray.ceilingShare - from
    );
    if (!(rise > 0)) {
      return writeHiddenPose(out);
    }
    // Только вверх и все медленнее: к концу полета капля почти стоит
    const climb = rise * (1 - (1 - life) ** spray.slowPower);
    // Веер: капля летит по прямой, чуть в сторону от оси столба
    const direction = hash01(particle.seed, cycle) < 0.5 ? -1 : 1;
    const fan = direction * lerp(spray.fanShare.min, spray.fanShare.max, hash01(particle.seed + 0.57, cycle));
    const outward = direction * geyser.width
      * lerp(spray.startOutShare.min, spray.startOutShare.max, hash01(particle.seed + 0.41, cycle))
      + climb * fan;
    const size = side * spray.sizeShare;
    return writePose(
      out,
      geyser.base.x + geyser.along.x * (from + climb) + geyser.across.x * outward,
      geyser.base.y + geyser.along.y * (from + climb) + geyser.across.y * outward,
      spray.alpha * smoothstep(0, spray.fadeInShare, life) * (1 - life) ** spray.fadePower * sprayStrength,
      size, size * spray.stretch,
      // Капля вытянута вдоль своего полета
      Math.atan2(-(geyser.along.x + geyser.across.x * fan), geyser.along.y + geyser.across.y * fan)
    );
  };

  // ---------------------------------------------------------------- источники

  const buildWisp = ({rng, view, liveGroups}) => {
    // Над источником, который уже парит, вторая струйка не поднимается
    const busy = new Set(liveGroups.map((group) => group.anchorIndex));
    const watchedBox = resolveWatchedBox(view);
    const anchor = pickWeighted(
      rng,
      anchors.filter(({index}) => !busy.has(index)),
      ({oval}) => resolveViewWeight(watchedBox, [oval], springs.inViewWeight)
    );
    if (!anchor) {
      return null;
    }

    const kindParams = springs[anchor.kind];
    const particles = [];
    let delayMs = 0;
    let durationMs = 0;
    for (let index = 0; index < kindParams.puffCount; index += 1) {
      const from = pickPointInOval(rng, anchor.oval, springs.spawnScale);
      const lifeMs = randomIn(rng, kindParams.lifeMs);
      particles.push({
        kind: WISP,
        layer: LAYER,
        texture: FIELD_BIOME_TEXTURES.PUFF,
        tint: springs.tint,
        delayMs,
        lifeMs,
        x: from.x,
        y: from.y,
        rise: side * randomIn(rng, kindParams.riseShare),
        sizeFrom: side * kindParams.sizeShare.from,
        sizeTo: side * kindParams.sizeShare.to * randomIn(rng, springs.sizeJitter),
        alpha: kindParams.alpha,
        rotation: pickStretchedRotation(rng, anchor.rise, springs.tiltDeg),
      });
      durationMs = Math.max(durationMs, delayMs + lifeMs);
      delayMs += randomIn(rng, kindParams.staggerMs);
    }
    return {durationMs, anchor, anchorIndex: anchor.index, particles};
  };

  const resolveWispPose = (group, particle, elapsedMs, out) => {
    const life = (elapsedMs - particle.delayMs) / particle.lifeMs;
    if (!(life > 0) || !(life < 1)) {
      return writeHiddenPose(out);
    }
    const {rise} = group.anchor;
    // Пар отрывается от воды и замедляется; чем выше, тем сильнее клонится по ветру
    const climb = particle.rise * (1 - (1 - life) ** springs.climbPower);
    const lean = particle.rise * springs.windLean * life * life;
    const width = lerp(particle.sizeFrom, particle.sizeTo, life ** springs.growPower);
    return writePose(
      out,
      particle.x + rise.x * climb + wind.x * lean,
      particle.y + rise.y * climb + wind.y * lean,
      particle.alpha * resolveLifeEnvelope(life, springs.fadeInShare, springs.fadeOutShare),
      width, width * springs.stretch,
      particle.rotation
    );
  };

  // ----------------------------------------------------------- трещины корки

  const buildFumarole = ({rng, view}) => {
    // Место — над игровыми ячейками, там, куда смотрит камера
    const box = resolveSpawnBox(context, view, {fieldMarginFactor: 0, viewMarginFactor: 0});
    let place = null;
    for (let attempt = 0; attempt < FUMAROLE_ATTEMPTS && !place; attempt += 1) {
      const point = pickPointInBox(rng, box);
      place = coverage.sample(point.x, point.y) >= fumaroles.minCoverage ? point : null;
    }
    if (!place) {
      return null;
    }

    // Вверх по экрану и чуть в сторону от середины картинки — как весь пар пейзажа
    const lean = params.riseLean * ((place.x - rect.left) / side - 0.5);
    const norm = Math.hypot(lean, 1);
    const rise = {x: lean / norm, y: -1 / norm};
    const particles = [];
    let delayMs = 0;
    let durationMs = 0;
    for (let index = 0; index < fumaroles.puffCount; index += 1) {
      const puff = {
        kind: FUMAROLE,
        layer: FIELD_BIOME_LAYER_KEYS.CELL,
        // Клуб — поверх теней
        sublayer: 1,
        texture: FIELD_BIOME_TEXTURES.PUFF,
        tint: fumaroles.tint,
        delayMs,
        lifeMs: randomIn(rng, fumaroles.lifeMs),
        // Куда клуб уходит от оси струйки: доля того, на сколько разошелся конус
        scatter: randomRange(rng, -fumaroles.scatterShare, fumaroles.scatterShare),
        // Вдоль струйки; наклон клуб набирает, пока поднимается
        rotation: pickStretchedRotation(rng, rise, 0),
        tilt: toRadians(randomRange(rng, -fumaroles.tiltDeg, fumaroles.tiltDeg)),
      };
      // Тень — тот же клуб, оставшийся на земле
      particles.push({...puff, kind: FUMAROLE_SHADOW, sublayer: 0, tint: fumaroles.shadow.tint}, puff);
      durationMs = Math.max(durationMs, delayMs + puff.lifeMs);
      delayMs += randomIn(rng, fumaroles.staggerMs);
    }
    return {
      durationMs,
      // Трещина — вершина конуса: все клубы струйки выходят из одной точки
      vent: place,
      rise,
      across: {x: -rise.y, y: rise.x},
      // Конус — один на струйку: ее высота и ширина у трещины и на вершине
      height: hexSize * randomIn(rng, fumaroles.riseFactor),
      sizeFrom: hexSize * fumaroles.sizeFactor.from,
      sizeTo: hexSize * fumaroles.sizeFactor.to * randomIn(rng, fumaroles.sizeJitter),
      particles,
    };
  };

  const resolveFumarolePose = (group, particle, elapsedMs, out) => {
    const life = (elapsedMs - particle.delayMs) / particle.lifeMs;
    if (!(life > 0) || !(life < 1)) {
      return writeHiddenPose(out);
    }
    // Клуб отрывается от трещины и замедляется: доля пройденной высоты струйки
    const climbShare = 1 - (1 - life) ** fumaroles.climbPower;
    // Ширина — по высоте над трещиной, а не по возрасту клуба: у трещины —
    // точка, и какой бы клуб струйки ни оказался выше, он шире. Это и есть конус
    const width = lerp(group.sizeFrom, group.sizeTo, climbShare ** fumaroles.growPower);
    const envelope = resolveLifeEnvelope(life, fumaroles.fadeInShare, fumaroles.fadeOutShare);
    // Чем выше, тем сильнее струйка клонится по ветру. Вбок от ее оси клуб
    // уходит вместе с шириной конуса: у самой трещины разброса нет
    const lean = group.height * fumaroles.windLean * climbShare * climbShare;
    const scatter = particle.scatter * (width - group.sizeFrom);
    const x = group.vent.x + wind.x * lean + group.across.x * scatter;
    const y = group.vent.y + wind.y * lean + group.across.y * scatter;
    if (particle.kind === FUMAROLE_SHADOW) {
      const {shadow} = fumaroles;
      return writePose(
        out, x, y + hexSize * shadow.dropFactor * climbShare, shadow.alpha * envelope,
        width * shadow.widthScale, width * shadow.widthScale * shadow.flatten, 0
      );
    }
    const climb = group.height * climbShare;
    return writePose(
      out, x + group.rise.x * climb, y + group.rise.y * climb, fumaroles.alpha * envelope,
      // У трещины клуб — тонкий штрих вдоль струйки, выше округляется и кренится
      width, width * lerp(fumaroles.stretch.from, fumaroles.stretch.to, climbShare),
      particle.rotation + particle.tilt * climbShare
    );
  };

  return [
    // Столбы нарисованы и ветра матча не слушают: при его смене дышат как дышали
    {usesWind: false, schedule: {standing: true}, buildGroup: buildColumns, resolvePose: resolveColumnPose},
    {
      tag: GEYSER_ERUPTION_TAG,
      schedule: {
        firstDelayMs: eruption.firstDelayMs, intervalMs: eruption.intervalMs, maxGroups: eruption.maxGroups,
      },
      buildGroup: buildEruption,
      resolvePose: resolveEruptionPose,
    },
    {
      schedule: {
        firstDelayMs: springs.firstDelayMs, intervalMs: springs.intervalMs, maxGroups: springs.maxGroups,
        prewarm: springs.prewarm,
      },
      buildGroup: buildWisp,
      resolvePose: resolveWispPose,
    },
    {
      tag: GEYSER_FUMAROLE_TAG,
      schedule: {
        firstDelayMs: fumaroles.firstDelayMs, intervalMs: fumaroles.intervalMs, maxGroups: fumaroles.maxGroups,
        prewarm: fumaroles.prewarm,
      },
      buildGroup: buildFumarole,
      resolvePose: resolveFumarolePose,
    },
  ];
};

const geyserSteam = {
  kind: GEYSER_STEAM_KIND,
  textures: [FIELD_BIOME_TEXTURES.PUFF, FIELD_BIOME_TEXTURES.DOT],
  createTracks,
};

export default geyserSteam;
